import apiClient from './apiClient';

/**
 * Stripe + signup API client.
 *
 * <p>Two surfaces share this module:
 * <ul>
 *   <li>Operator → Stripe Settings (gated SUPER_ADMIN)</li>
 *   <li>Public → signup-config + signup POST (no auth)</li>
 * </ul>
 */

const opBase = '/api/v1.0/operator/stripe';
const publicBase = '/api/v1.0/signup';

// ---- operator -------------------------------------------------

export const getStripeSettings = () =>
  apiClient.get(`${opBase}/settings`).then((r) => r.data);

export const updateStripeSettings = (payload) =>
  apiClient.put(`${opBase}/settings`, payload).then((r) => r.data);

export const testStripeConnection = () =>
  apiClient.post(`${opBase}/test`).then((r) => r.data);

/** Operator-only — creates a real Stripe Checkout session for a
 *  chosen plan WITHOUT creating an org or user, so the operator
 *  can dry-run the full payment flow before flipping the master
 *  signup flag on. */
export const startTestCheckout = (planCode, testEmail) =>
  apiClient
    .post(`${opBase}/test-checkout`, { planCode, testEmail })
    .then((r) => r.data);

/** All subscription plans — operator-only. Used by the test-signup
 *  panel to populate the plan dropdown. */
export const listSubscriptionPlansAdmin = () =>
  apiClient.get('/api/v1.0/subscription-plans').then((r) => r.data);

// ---- public (no auth) -----------------------------------------

export const getSignupConfig = () =>
  apiClient.get(`${publicBase}/config`).then((r) => r.data);

export const submitSignup = (payload) =>
  apiClient.post(publicBase, payload).then((r) => r.data);

/** Public "Contact sales" inquiry from the signup page. Emails the
 *  prospect's details to the plan's targeted sales inbox. */
export const submitSalesInquiry = (payload) =>
  apiClient.post(`${publicBase}/contact-sales`, payload).then((r) => r.data);

/** Verify a signup with the 6-character OTP from the activation email.
 *  Returns { ok, token, orgSlug } on success. */
export const verifySignupOtp = ({ email, orgSlug, otp }) =>
  apiClient.post(`${publicBase}/verify-otp`, { email, orgSlug, otp }).then((r) => r.data);

/** Re-send the activation email (new OTP) for an unverified account. */
export const resendSignupVerification = ({ email, orgSlug }) =>
  apiClient.post(`${publicBase}/resend-verification`, { email, orgSlug }).then((r) => r.data);

/** Look up a Stripe Checkout session by id and return a sanitised
 *  summary for the success page. Backend joins the Stripe response
 *  to the local org + plan so we know what the user actually
 *  bought (in plain English, not Stripe codes). */
export const getCheckoutSummary = (sessionId) =>
  apiClient
    .get(`${publicBase}/checkout-summary`, { params: { session_id: sessionId } })
    .then((r) => r.data);
